import type {
  RoutingSkuTargetRecord,
  SmartDecisionMinute,
  SmartOverviewTarget,
  SmartRole,
  SmartTargetState,
} from '@nadoutong/contracts';

export const SMART_ROLES: readonly SmartRole[] = ['self_pool', 'external', 'reserve'];
export const SMART_STATES: readonly SmartTargetState[] = [
  'warming',
  'serving',
  'quarantined',
  'half_open',
  'retired',
];

export function isSmartRole(value: string): value is SmartRole {
  return SMART_ROLES.some((role) => role === value);
}

export function isSmartState(value: string): value is SmartTargetState {
  return SMART_STATES.some((state) => state === value);
}

/**
 * Join a pool row to an overview row. Overview keys on hashed identity;
 * the console matches on upstream + channel slug, then unique upstream.
 */
export const SMART_DEFAULT_POOL_GROUP = 'default_pool';
export const SMART_SYSTEM_DEFAULT_CONFIG_ID = 'rcfg_system_default';

export interface SmartOverviewGroup {
  readonly key: string;
  readonly configId: string | null;
  readonly configName: string | null;
  readonly rows: readonly SmartOverviewTarget[];
}

export function groupSmartOverviewTargets(
  rows: readonly SmartOverviewTarget[],
): readonly SmartOverviewGroup[] {
  const groups = new Map<string, SmartOverviewTarget[]>();
  const names = new Map<string, string | null>();
  for (const row of rows) {
    const key = row.config_id ?? SMART_DEFAULT_POOL_GROUP;
    const list = groups.get(key);
    if (list === undefined) groups.set(key, [row]);
    else list.push(row);
    if (!names.has(key)) names.set(key, row.config_name ?? null);
  }
  const rank = (key: string): number => {
    if (key === SMART_DEFAULT_POOL_GROUP) return 0;
    if (key === SMART_SYSTEM_DEFAULT_CONFIG_ID) return 1;
    return 2;
  };
  return [...groups.entries()]
    .sort((left, right) => {
      const byRank = rank(left[0]) - rank(right[0]);
      if (byRank !== 0) return byRank;
      return left[0].localeCompare(right[0]);
    })
    .map(([key, grouped]) => ({
      key,
      configId: key === SMART_DEFAULT_POOL_GROUP ? null : key,
      configName: names.get(key) ?? null,
      rows: grouped,
    }));
}

export function matchSmartOverviewTarget(
  rows: readonly SmartOverviewTarget[],
  target: Pick<RoutingSkuTargetRecord, 'upstream_id' | 'upstream_channel_slug'>,
): SmartOverviewTarget | null {
  const exact = rows.find((row) =>
    row.upstream_id === target.upstream_id &&
    row.channel_slug === target.upstream_channel_slug,
  );
  if (exact !== undefined) return exact;
  const byUpstream = rows.filter((row) => row.upstream_id === target.upstream_id);
  const [only] = byUpstream;
  return byUpstream.length === 1 && only !== undefined ? only : null;
}

export function formatRatio(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(1)}%`;
}

/** Save-rate from counts. `refires === 0` (or missing) is "—" not 0%. */
export function formatRefireSaveRate(saved: number, refires: number | undefined): string {
  if (refires === undefined || refires <= 0 || !Number.isFinite(saved)) return '—';
  return formatRatio(saved / refires);
}

export function formatRefireSaveWindows(
  saved: { readonly m1: number; readonly h1?: number; readonly day1?: number },
  refires: { readonly m1: number; readonly h1: number; readonly day1: number } | undefined,
): string {
  if (refires === undefined) {
    return [formatRefireSaveRate(saved.m1, undefined), '—', '—'].join(' / ');
  }
  return [
    formatRefireSaveRate(saved.m1, refires.m1),
    formatRefireSaveRate(saved.h1 ?? 0, refires.h1),
    formatRefireSaveRate(saved.day1 ?? 0, refires.day1),
  ].join(' / ');
}

export function formatTtftPair(p50: number | null, p95: number | null): string {
  const left = p50 === null ? '—' : String(Math.round(p50));
  const right = p95 === null ? '—' : String(Math.round(p95));
  return `${left} / ${right}`;
}

export function formatPosterior(mean: number, lo: number, hi: number): string {
  return `${mean.toFixed(2)} [${lo.toFixed(2)}–${hi.toFixed(2)}]`;
}

export function formatUsd(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—';
  return value.toFixed(4);
}

export function formatMultiplier(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—';
  return value.toFixed(4);
}

export interface SmartShareStack {
  readonly labels: readonly string[];
  readonly keys: readonly string[];
  readonly values: Readonly<Record<string, readonly number[]>>;
}

function minuteLabel(minute: string): string {
  const time = minute.includes('T') ? minute.slice(minute.indexOf('T') + 1) : minute;
  return time.length >= 5 ? time.slice(0, 5) : time;
}

/** Convert `decisions_per_minute` counts into per-minute shares (0–1) per target. */
export function stackedShareFromDecisions(
  minutes: readonly SmartDecisionMinute[],
): SmartShareStack {
  const keys = minutes.reduce<readonly string[]>((acc, bucket) => {
    const extra = Object.keys(bucket.by_target).filter((key) => !acc.includes(key));
    return extra.length === 0 ? acc : [...acc, ...extra];
  }, []);
  const labels = minutes.map((bucket) => minuteLabel(bucket.minute));
  const values: Readonly<Record<string, readonly number[]>> = Object.fromEntries(
    keys.map((key) => [
      key,
      minutes.map((bucket) => {
        const total = Object.values(bucket.by_target).reduce((sum, count) => sum + count, 0);
        if (total <= 0) return 0;
        return (bucket.by_target[key] ?? 0) / total;
      }),
    ]),
  );
  return { labels, keys, values };
}
